import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { NotificationService } from 'src/app/core/services/notification.service';
import { PageHeaderComponent } from 'src/app/shared/components/page-header/page-header.component';
import {
  ContributionUserService,
  UserContributionFilters,
} from 'src/app/shared/services/contribution-user.service';
import {
  Contribution,
  ContributionStatus,
  PaginationParams,
} from 'src/app/shared/models/contribution.models';
import { SelectTripModalComponent } from '../components/select-trip-modal/select-trip-modal.component';
import { SelectTripModalService } from '../components/select-trip-modal/select-trip-modal.service';

@Component({
  selector: 'app-contribution-list',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    PageHeaderComponent,
    ButtonModule,
    TableModule,
    ToastModule,
    SelectModule,
    TooltipModule,
    TagModule,
    FormsModule,
    SelectTripModalComponent,
  ],
  templateUrl: './contribution-list.component.html',
})
export class ContributionListComponent implements OnInit {
  private contributionService = inject(ContributionUserService);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private selectTripModalService = inject(SelectTripModalService);
  private translocoService = inject(TranslocoService);

  contributions: Contribution[] = [];
  loading = false;

  // Always show company contributions
  scope = 'company' as const;

  // Filter
  statusFilter: ContributionStatus | null = null;
  statusOptions: { label: string; value: ContributionStatus | null }[] = [];
  availableStatuses: ContributionStatus[] = [];

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalRecords = 0;
  totalPages = 0;

  pageSizeOptions = [
    { label: '20', value: 20 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
  ];

  ngOnInit(): void {
    this.initializeOptions();
    this.loadContributions();

    // Update options when language changes
    this.translocoService.langChanges$.subscribe(() => {
      this.initializeOptions();
    });
  }

  private initializeOptions(): void {
    this.updateStatusOptions();
  }

  private updateStatusOptions(): void {
    // Build status options based on available statuses
    const allStatuses = [
      { label: this.translocoService.translate('contribute.status.PENDING_PAYMENT'), value: ContributionStatus.PENDING_PAYMENT },
      { label: this.translocoService.translate('contribute.status.PAID'), value: ContributionStatus.PAID },
      { label: this.translocoService.translate('contribute.status.FAILED'), value: ContributionStatus.FAILED },
      { label: this.translocoService.translate('contribute.status.CANCELED'), value: ContributionStatus.CANCELED },
      { label: this.translocoService.translate('contribute.status.REFUNDED'), value: ContributionStatus.REFUNDED },
    ];

    // Filter to only show statuses that exist in the data
    const filteredStatuses = this.availableStatuses.length > 0
      ? allStatuses.filter(opt => this.availableStatuses.includes(opt.value))
      : allStatuses;

    // Add "All" option only if there are multiple statuses
    this.statusOptions = filteredStatuses.length > 1
      ? [{ label: this.translocoService.translate('contributions.statusOptions.all'), value: null }, ...filteredStatuses]
      : filteredStatuses;

    // Reset filter if current filter is no longer available
    if (this.statusFilter && !this.availableStatuses.includes(this.statusFilter)) {
      this.statusFilter = null;
    }
  }

  loadContributions(): void {
    this.loading = true;

    const filters: UserContributionFilters = {};
    if (this.statusFilter) filters.status = this.statusFilter;

    const pagination: PaginationParams = {
      page: this.currentPage,
      limit: this.pageSize,
    };

    this.contributionService
      .listContributions(this.scope, filters, pagination)
      .subscribe({
        next: (response) => {
          this.contributions = response.data;
          this.totalRecords = response.pagination.total;
          this.totalPages = response.pagination.totalPages;
          // Update available statuses and filter options
          if (response.availableStatuses) {
            this.availableStatuses = response.availableStatuses;
            this.updateStatusOptions();
          }
          this.loading = false;
        },
        error: () => {
          this.notificationService.error('Failed to load contributions');
          this.loading = false;
        },
      });
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadContributions();
  }

  resetFilters(): void {
    this.statusFilter = null;
    this.currentPage = 1;
    this.loadContributions();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadContributions();
  }

  onPageSizeChange(): void {
    this.currentPage = 1;
    this.loadContributions();
  }

  viewDetails(contributionId: string): void {
    this.router.navigate(['/home/contributions', contributionId]);
  }

  downloadCertificate(contribution: Contribution): void {
    if (contribution.status !== ContributionStatus.PAID) {
      this.notificationService.error('Certificate only available for paid contributions');
      return;
    }
    const url = this.contributionService.getCertificateUrl(contribution.id);
    window.open(url, '_blank');
  }

  downloadInvoice(): void {
    this.notificationService.success('Invoice download will be available soon');
  }

  getStatusClasses(status: ContributionStatus): string {
    const classMap: Record<ContributionStatus, string> = {
      [ContributionStatus.PENDING_PAYMENT]: 'bg-yellow-100 text-yellow-700',
      [ContributionStatus.PAID]: 'bg-green-100 text-green-700',
      [ContributionStatus.FAILED]: 'bg-red-100 text-red-700',
      [ContributionStatus.CANCELED]: 'bg-gray-100 text-gray-700',
      [ContributionStatus.REFUNDED]: 'bg-blue-100 text-blue-700',
    };
    return classMap[status] || 'bg-gray-100 text-gray-700';
  }

  navigateToTripImpact(tripId: string): void {
    this.router.navigate(['/home/trips', tripId, 'impact']);
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  formatAmount(amount: number | string, showVatLabel = false): string {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    const exclVat = this.translocoService.translate('contributions.table.exclVat');
    return showVatLabel ? `€${numAmount.toFixed(2)} ${exclVat}` : `€${numAmount.toFixed(2)}`;
  }

  getProjectNames(contribution: Contribution): string {
    return contribution.projectContributions
      .map((pc) => pc.projectName)
      .join(', ');
  }

  getShortId(id: string): string {
    return id.substring(0, 8);
  }

  canDownload(contribution: Contribution): boolean {
    return contribution.status === ContributionStatus.PAID;
  }

  openNewContributionModal(): void {
    this.selectTripModalService.open();
  }
}
